<script lang="ts">
/** Risk levels this pill can render — adapt backend enums via statusMaps. */
export type GaiaRiskLevel = 'none' | 'low' | 'medium' | 'high';
</script>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { gaiaFontSize, gaiaFontWeight, gaiaRiskTint, gaiaStatus } from '../themes/tokens';
import { getContrastTextColor } from '@/utils/displayAiResponse';

const props = defineProps({
  /** Playbook risk level — adapt backend enums via statusMaps.playbookRiskLevelMap. */
  level: {
    type: String as PropType<GaiaRiskLevel>,
    required: true,
  },
  /** Fully rounded (the detail fields list); default is the radius-8 card chip. */
  pill: {
    type: Boolean,
    default: false,
  },
  /**
   * Backend-configured surface colour, overriding `level`'s palette.
   *
   * The one deliberate exception to "change the token, not the instance": a
   * review's evaluation answers are configurable per organisation
   * (`evaluation_response_type.values[].color`), so the colour is org *data*,
   * not a design decision, and no token can name it. Pass the stored hex; the
   * foreground is derived for contrast rather than chosen. Leave it unset —
   * which is every other call site — and the level palette applies.
   */
  color: {
    type: String,
    default: undefined,
  },
});

// Tinted risk surfaces from the Kanban mock — foreground from the status
// tokens, the matching wash and hairline from gaiaRiskTint.
const palette = {
  none: {
    color: gaiaStatus.success,
    bg: gaiaRiskTint.successSurface,
    border: gaiaRiskTint.successBorder,
  },
  low: {
    color: gaiaStatus.success,
    bg: gaiaRiskTint.successSurface,
    border: gaiaRiskTint.successBorder,
  },
  medium: {
    color: gaiaStatus.warning,
    bg: gaiaRiskTint.warningSurface,
    border: gaiaRiskTint.warningBorder,
  },
  high: { color: gaiaStatus.risk, bg: gaiaRiskTint.riskSurface, border: gaiaRiskTint.riskBorder },
} as const;

const colors = computed(() => {
  if (!props.color) return palette[props.level];
  // A configured answer colour paints the surface; the text and hairline are
  // derived from it so an org can't produce an unreadable chip.
  return {
    color: getContrastTextColor(props.color),
    bg: props.color,
    border: props.color,
  };
});

// Two chip forms, each with its own step — the pill is denser and bolder, so
// its type is a selector-bound rule rather than a GaiaText prop.
const cardSize = gaiaFontSize.micro;
const pillSize = gaiaFontSize.nano;
const semibold = String(gaiaFontWeight.semibold);
const bold = String(gaiaFontWeight.bold);
</script>

<template>
  <!-- Risk chip from the Reviews Kanban mock — card form ("3 risks
       identified") and pill form (High / Low in the fields list). -->
  <n-flex
    inline
    align="center"
    :size="0"
    :wrap="false"
    class="gaia-risk-pill"
    :class="{ pill: props.pill }"
    :style="{ color: colors.color, background: colors.bg, borderColor: colors.border }"
  >
    <slot />
  </n-flex>
</template>

<style scoped>
.gaia-risk-pill {
  height: 22px;
  padding: 0 8px;
  border-radius: 8px;
  border: 1px solid;
  font-size: v-bind(cardSize);
  font-weight: v-bind(semibold);
  white-space: nowrap;
}

.gaia-risk-pill.pill {
  height: 20px;
  border-radius: 20px;
  font-size: v-bind(pillSize);
  font-weight: v-bind(bold);
}
</style>
